import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, MapPin, Search, AlertCircle, Phone, ChevronRight, Activity, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const PublicPortal = () => {
  const [refDossier, setRefDossier] = useState('');
  const navigate = useNavigate();

  const handleSuivi = () => {
    if (refDossier.trim()) {
      navigate(`/suivi?ref=${refDossier.trim()}`);
    } else {
      navigate('/suivi');
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      {/* Hero Section - Ultra clean business look */}
      <section className="relative bg-gradient-to-b from-[#0A192F] to-[#1E3A8A] text-white overflow-hidden border-b-4 border-amber-500">
        {/* Subtle grid pattern for business feel */}
        <div className="absolute inset-0 z-0 opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        
        <div className="container mx-auto px-4 py-20 lg:py-28 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30 mb-8 backdrop-blur-sm shadow-sm">
                <Activity className="w-4 h-4" />
                <span>Projet proposé au Gouvernement provincial du Lualaba</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight mb-6">
                Les services et les actions au <span className="text-amber-400">Lualaba</span>
              </h1>
              <p className="text-lg text-blue-100 mb-10 max-w-2xl leading-relaxed font-medium">
                Découvrez les projets documentés et accédez aux démarches disponibles.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/demander-aide">
                  <Button className="w-full sm:w-auto bg-[#0A192F] border-2 border-white hover:bg-white hover:text-[#0A192F] text-white font-bold h-14 px-8 rounded-xl shadow-lg text-base transition-all hover:translate-y-[-2px]" data-testid="hero-demarche-btn">
                    Commencer une démarche <ArrowUpRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/realisations">
                  <Button variant="outline" className="w-full sm:w-auto bg-white/5 hover:bg-white/10 text-white border-white/20 h-14 px-8 rounded-xl font-semibold backdrop-blur-sm text-base transition-all" data-testid="hero-realisation-btn">
                    Voir les réalisations
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden border border-white/20 shadow-2xl shadow-blue-900/50 group">
                <div className="absolute inset-0 bg-blue-900/20 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                <img 
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_services-lualaba/artifacts/61d48a447ef85409_IMG_5091.jpeg"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1531206715517-5c0ba140b4b8?q=80&w=600&auto=format&fit=crop"; }}
                  alt="Gouverneure Fifi Masuka sur le terrain" 
                  className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/90 to-transparent p-8 pt-24 z-20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">Leadership sur le terrain</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Fifi Masuka Saini</h3>
                  <p className="text-blue-200 text-sm font-medium leading-relaxed">Gouverneure de la Province du Lualaba</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Core Actions Bento Grid - Clean and Structured */}
      <section className="py-16 -mt-10 relative z-30 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <Link to="/demander-aide" className="group" data-testid="action-card-aide">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 h-full flex flex-col items-start relative overflow-hidden">
                <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors border border-blue-100">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Demander de l'aide</h3>
                <p className="text-slate-500 text-sm mb-6 flex-grow leading-relaxed">
                  Assistance sociale, médicale ou financière pour vous ou un proche.
                </p>
                <span className="text-blue-600 font-semibold text-sm flex items-center group-hover:text-blue-800 mt-auto">
                  Remplir le formulaire <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>

            <Link to="/trouver-service" className="group" data-testid="action-card-service">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 h-full flex flex-col items-start relative overflow-hidden">
                <div className="w-12 h-12 bg-slate-100 text-slate-700 rounded-xl flex items-center justify-center mb-5 group-hover:bg-slate-700 group-hover:text-white transition-colors border border-slate-200">
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Trouver un service</h3>
                <p className="text-slate-500 text-sm mb-6 flex-grow leading-relaxed">
                  Hôpitaux, écoles, guichets et structures habilitées à proximité.
                </p>
                <span className="text-slate-600 font-semibold text-sm flex items-center group-hover:text-slate-800 mt-auto">
                  Consulter l'annuaire <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>

            <Link to="/signaler" className="group" data-testid="action-card-signaler">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 h-full flex flex-col items-start relative overflow-hidden">
                <div className="w-12 h-12 bg-indigo-50 text-indigo-700 rounded-xl flex items-center justify-center mb-5 group-hover:bg-indigo-600 group-hover:text-white transition-colors border border-indigo-100">
                  <AlertCircle className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Signaler un problème</h3>
                <p className="text-slate-500 text-sm mb-6 flex-grow leading-relaxed">
                  Infrastructures dégradées, fuites d'eau ou pannes d'éclairage.
                </p>
                <span className="text-indigo-600 font-semibold text-sm flex items-center group-hover:text-indigo-800 mt-auto">
                  Faire un signalement <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>

            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800 flex flex-col items-start group" data-testid="action-card-suivi">
              <div className="w-12 h-12 bg-white/10 text-white rounded-xl flex items-center justify-center mb-5 border border-white/20">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Suivre mon dossier</h3>
              <p className="text-slate-400 text-sm mb-6 flex-grow leading-relaxed">
                Consultez l'avancement de vos demandes avec votre référence.
              </p>
              <div className="w-full mt-auto">
                <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700 focus-within:border-white transition-colors shadow-inner">
                  <input 
                    type="text" 
                    placeholder="N° Référence" 
                    className="bg-transparent border-none text-white text-sm w-full px-3 py-2 focus:outline-none placeholder-slate-500 uppercase" 
                    value={refDossier}
                    onChange={(e) => setRefDossier(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSuivi()}
                  />
                  <button 
                    className="bg-white hover:bg-slate-200 text-[#0A192F] rounded-md px-3 py-2 transition-colors font-bold text-sm"
                    onClick={handleSuivi}
                  >
                    Suivre
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Real Sourced Achievements */}
      <section className="py-20 bg-white border-t border-slate-200" id="realisations">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div className="max-w-2xl border-l-4 border-blue-900 pl-6">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Réalisations documentées</h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                Archives photographiques et descriptives des projets d'infrastructures et actions sociales au Lualaba.
              </p>
            </div>
            <Link to="/realisations">
              <Button variant="outline" className="mt-6 md:mt-0 font-bold border-slate-300 text-slate-700 hover:border-blue-900 hover:text-blue-900 bg-white shadow-sm h-11 px-6 rounded-xl" data-testid="view-all-projects-btn">
                Consulter le catalogue <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Project Card 1 */}
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col sm:flex-row" data-testid="project-card-1">
              <div className="w-full sm:w-2/5 relative overflow-hidden">
                <img 
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_services-lualaba/artifacts/f569a8c8dacd3127_IMG_5090.jpeg"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=400&auto=format&fit=crop"; }}
                  alt="Soutien à l'éducation" 
                  className="w-full h-full object-cover min-h-[240px] group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-md text-xs font-bold text-[#0A192F] shadow-sm border border-slate-100">
                  Éducation
                </div>
              </div>
              <div className="p-6 sm:w-3/5 flex flex-col justify-center">
                <div className="flex items-center gap-2 text-xs font-bold mb-3">
                  <span className="text-[#0A192F] bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">Action Réalisée</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-500 uppercase tracking-wider">Kolwezi</span>
                </div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-3 group-hover:text-[#0A192F] transition-colors leading-snug">Rénovation du Lycée Mwanga</h3>
                <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                  Remise officielle des bâtiments rénovés. L'établissement dispose de 40 salles de classe pour 1 200 élèves.
                </p>
                <div className="flex items-center text-xs text-slate-500 font-semibold pt-4 border-t border-slate-100 mt-auto">
                  <FileText className="w-4 h-4 mr-1.5 text-blue-900" /> Source documentaire : Réf. A03
                </div>
              </div>
            </div>

            {/* Project Card 2 */}
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col sm:flex-row" data-testid="project-card-2">
              <div className="w-full sm:w-2/5 relative overflow-hidden">
                <img 
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_services-lualaba/artifacts/1290ea02542e8d86_IMG_5089.jpeg"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1590481232840-02e3b8b64e03?q=80&w=400&auto=format&fit=crop"; }}
                  alt="Prise en charge sociale" 
                  className="w-full h-full object-cover min-h-[240px] group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-md text-xs font-bold text-[#0A192F] shadow-sm border border-slate-100">
                  Social
                </div>
              </div>
              <div className="p-6 sm:w-3/5 flex flex-col justify-center">
                <div className="flex items-center gap-2 text-xs font-bold mb-3">
                  <span className="text-[#0A192F] bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">Action Réalisée</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-500 uppercase tracking-wider">Musompo</span>
                </div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-3 group-hover:text-[#0A192F] transition-colors leading-snug">Orphelinat de Musompo</h3>
                <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                  Inauguration du Complexe Maman Denise Nyakeru Tshisekedi, dédié à l'accueil et l'éducation des enfants vulnérables.
                </p>
                <div className="flex items-center text-xs text-slate-500 font-semibold pt-4 border-t border-slate-100 mt-auto">
                  <FileText className="w-4 h-4 mr-1.5 text-blue-900" /> Source documentaire : Réf. A06
                </div>
              </div>
            </div>
          </div>

          {/* Large Highlight Card */}
          <div className="bg-[#0A192F] rounded-2xl overflow-hidden shadow-xl flex flex-col lg:flex-row relative group">
            <div className="lg:w-1/2 p-10 lg:p-12 flex flex-col justify-center z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/20 mb-6 w-fit">
                Proximité
              </div>
              <h3 className="text-3xl font-extrabold text-white mb-4 leading-tight">À l'écoute de la population</h3>
              <p className="text-slate-300 mb-8 leading-relaxed text-lg">
                Échanges directs avec les citoyens pour identifier les besoins réels et documenter l'état des infrastructures locales.
              </p>
              <Link to="/realisations">
                <Button className="bg-white hover:bg-slate-100 text-[#0A192F] font-bold h-12 w-fit px-8 rounded-xl border-none">
                  Voir l'historique complet <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="lg:w-1/2 relative min-h-[300px] lg:min-h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] to-transparent z-10 hidden lg:block"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] to-transparent z-10 lg:hidden"></div>
              <img 
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_services-lualaba/artifacts/e28ab494a8f9fb53_IMG_5087.jpeg" 
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1531206715517-5c0ba140b4b8?q=80&w=800&auto=format&fit=crop"; }}
                alt="Rencontre avec la population" 
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Offline/Assistance Note - Clean Business Style */}
      <section className="bg-slate-100 text-slate-900 py-12 border-t border-slate-200">
        <div className="container mx-auto px-4 max-w-6xl flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-start gap-5 max-w-3xl">
            <div className="mt-1 bg-blue-100 p-3 rounded-xl border border-blue-200 shrink-0">
              <Phone className="w-6 h-6 text-blue-900" />
            </div>
            <div>
              <h4 className="text-xl font-bold mb-2">Besoin d'assistance pour vos démarches ?</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Nos agents d'accueil dans les guichets provinciaux peuvent enregistrer votre demande et vous remettre un récépissé papier sécurisé.
              </p>
            </div>
          </div>
          <Link to="/trouver-service">
            <Button className="bg-[#0A192F] text-white hover:bg-blue-900 whitespace-nowrap font-bold h-12 px-6 rounded-xl border-none">
              Trouver un guichet
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PublicPortal;
